import type { ReactNode } from 'react';
import { AvailabilityNotice } from '@/modules/ai';
import type { EffectiveAiSettings } from '@/api/schemas/ai';
import { Textarea } from '@/shared/ui/textarea';

/**
 * THE AI HALF OF THE PAGE, AND WHAT IT SAYS BEFORE IT OFFERS ANYTHING.
 *
 * ---------------------------------------------------------------------------
 * THE AVAILABILITY IS SHOWN FIRST
 * ---------------------------------------------------------------------------
 * A merchant is told whether the assistant can work BEFORE they are asked to
 * describe their catalogue. When it cannot, the reason is in plain words and
 * the manual forms still work — the assistant is an aid, never a requirement.
 *
 * When the availability read FAILS, the page does not guess: it says the
 * availability could not be read, keeps the assistant out of reach, and leaves
 * the manual forms exactly as they were.
 */
export type AiAvailabilityPanelProps = {
  readonly settings: EffectiveAiSettings | null;
  readonly error: string | null;
  readonly onRetry: () => void;
};

export function AiAvailabilityPanel({
  settings,
  error,
  onRetry,
}: AiAvailabilityPanelProps): ReactNode {
  if (error !== null) {
    return (
      <div className="space-y-1">
        <p className="text-sm text-content">
          The assistant&rsquo;s availability could not be read, so the assistant is out of reach for
          now.
        </p>
        <p className="text-sm text-content-muted">{error}</p>
        <button
          type="button"
          className="text-sm text-accent underline underline-offset-2"
          onClick={onRetry}
        >
          Try reading it again
        </button>
      </div>
    );
  }

  if (settings === null) return null;

  return <AvailabilityNotice availability={settings.availability} reason={settings.reason} />;
}

/**
 * "Describe what you sell."
 *
 * The one field the whole assistant flow starts from. It is required by the
 * API — the instruction is at least three characters — and the button that
 * uses it stays disabled until there is something to send.
 */
export type InstructionsFieldProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
};

export function InstructionsField({ value, onChange }: InstructionsFieldProps): ReactNode {
  return (
    <Textarea
      label="Describe what you sell"
      hint="Plain language is enough: “handmade ceramic mugs and plates, in four colours”."
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
